<template>
  <nav class="bg-white border-b shadow-sm navbar-dropdown">
    <div class="px-4 sm:px-6 lg:px-8 navbar-content">
      <div class="flex items-center justify-between h-16 -mb-px">
        <!-- Search -->
        <form>
          <div class="flex">
            <div
              v-if="isDevelopment === 'development'"
              class="relative mx-auto text-gray-600 w-80"
            >
              <input
                class="h-10 px-5 pr-16 text-sm bg-white border border-slate-300 md:w-50 lg:w-80 w-50 focus:outline-none"
                type="search"
                name="search"
                placeholder="Search"
              >
              <button
                type="submit"
                class="absolute top-0 mt-3 mr-4 text-gray-600 right-20 ml:right-20 lg:right-0"
              >
                <SearchIcon />
              </button>
            </div>
          </div>
        </form>

        <!-- Header: Right side//Revamped NavBar -->
        <div
          v-if="showRevampedNavbar"
          class="flex items-center gap-x-3"
        >
          <div class="flex items-center gap-x-3">
            <div
              v-if="user.is_zuno_engineer && userFiles.length > 0"
              class="relative navbar-dropdown inline-flex pr-3"
            >
              <button
                ref="downloadButtonRef"
                class="flex items-center justify-center w-8 h-8 transition duration-150 bg-gray-100 rounded-full hover:bg-gray-200"
                :class="{ 'text-gray-700 bg-gray-200': showItemNamesFilter }"
                @click="toggleDownloadsExpansion"
              >
                <span class="sr-only">Notifications</span>
                <span class="text-main-primary">
                  <CloudDownload />
                </span>
              </button>
              <div
                v-if="showItemNamesFilter"
                ref="downloadMenuRef"
                class="absolute w-[400px] right-0 top-full mt-1 dropdown-menu"
              >
                <div class="mt-8 bg-white border rounded-lg shadow-md border-slate-100">
                  <h4 class="py-4 mx-6 text-main-primary">
                    Downloads
                  </h4>
                  <hr class="h-0.5 bg-gray-100 border-0">
                  <div v-if="loading">
                    <LoaderComponent loader-text="Loading download files..." />
                  </div>
                  <div
                    v-else
                    class="relative max-h-[250px] pr-6 overflow-y-hidden hover:overflow-y-auto"
                  >
                    <div
                      v-for="(item, itemIndex) in listOfFiles"
                      :key="itemIndex"
                    >
                      <template v-if="item.status === 'pending' || item.status === 'completed'">
                        <div class="flex justify-between gap-3">
                          <div :class="`flex items-center gap-2  py-2 px-3 w-full text-primary`">
                            <div class="p-2 rounded-md bg-gray-50">
                              <ExcelIcon />
                            </div>
                            <div>
                              <p class="p-0 text-sm text-main-primary whitespace-nowrap">
                                {{ item.name }} <br>
                                <span class="p-0 text-xs">{{ $moment(item.created_on).format('DD/MM/YYYY HH:mm:ss')
                                }}</span>
                              </p>
                            </div>
                          </div>
                          <div
                            v-if="item.status === 'completed'"
                            class="flex items-center justify-between gap-x-3"
                          >
                            <button @click="downloadFile(item.file)">
                              <DownloadIcon custom-style="w-4 h-4 text-main-primary cursor-pointer" />
                            </button>
                            <button @click="deleteFile(item.id)">
                              <TrashIcon custom-style="w-4 h-4  text-red-700 cursor-pointer" />
                            </button>
                          </div>
                          <div
                            v-if="item.status === 'pending'"
                            class="flex items-center justify-between text-main-primary gap-x-3"
                          >
                            <SpinnerLarge />
                            <button @click="deleteFile(item.id)">
                              <TrashIcon custom-style="w-4 h-4  text-red-700 cursor-pointer" />
                            </button>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                  <hr class="h-0.5 my-4 bg-gray-100 border-0">
                  <div class="flex items-center justify-end w-full gap-2 px-4">
                    <button
                      class="px-4 py-2 mb-5 mr-3 text-sm text-white rounded-sm shadow-sm bg-main-primary"
                      @click="showItemNamesFilter = false"
                    >
                      Close
                    </button>
                    <!-- <PrimaryButton classes="px-16 py-1" @click="showItemNamesFilter = false;">Close</PrimaryButton> -->
                  </div>
                </div>
              </div>
            </div>
            <!-- Notifications button -->
            <div
              v-if="isDevelopment === 'development'"
              class="relative inline-flex px-3"
            >
              <button
                class="flex items-center justify-center w-8 h-8 transition duration-150 bg-gray-100 rounded-full hover:bg-gray-200"
              >
                <span class="sr-only">Notifications</span>
                <span class="text-gray-500">
                  <BellSmall />
                </span>
                <span class="absolute top-0 right-3 w-2.5 h-2.5 bg-red-500 border-2 border-white rounded-full" />
              </button>
            </div>

            <div
              class="relative navbar-dropdown"
            >
              <NotificationIcon
                :has-notification="hasUnreadNotifications"
                @click="handleNotification"
              />
            </div>

            <div
              class="relative navbar-dropdown"
            >
              <LanguagePicker
                :use-new-icon="showRevampedNavbar"
                :user-current-language="currentLanguage"
                @language-changed="handleLanguageChange"
              />
            </div>

            <div class="relative navbar-dropdown">
              <button
                class="inline-flex items-center justify-center Centergroup"
                aria-haspopup="true"
              >
                <span class="text-sm font-medium no-localize-translate group-hover:text-gray-800">{{ user.first_name }}
                  {{
                    user.last_name
                  }}</span>
              </button>
            </div>
          </div>

          <div class="relative navbar-dropdown">
            <button
              ref="dropdownButtonRef"
              class="flex items-center outline-none gap-x-1"
              @click="toggleDropdown"
            >
              <img
                v-if="subdomain === 'yokogawa'"
                src="https://res.cloudinary.com/dbacjggmt/image/upload/v1690978496/AHI_qrkfdd.jpg"
                alt="Veridis Logo"
                class="h-auto w-14"
              >
              <ChevronNav
                :custom-style="[
                  'h-6 w-6 transition-transform duration-400',
                  menuOpened ? 'rotate-180' : '',
                ].join(' ')"
              />
            </button>
            <div
              v-if="menuOpened"
              ref="dropdownMenuRef"
              class="absolute right-0 top-full min-w-[220px] mt-1 rounded-md p-4 bg-white border border-slate-300 flex flex-col gap-y-2 dropdown-menu"
            >
              <div class="flex flex-col pb-3 border-b gap-y-2">
                <span class="text-sm text-gray-300">Profile</span>
                <div class="flex flex-col text-sm gap-y-2">
                  <NuxtLink to="/dashboard/settings/general">
                    My Profile
                  </NuxtLink>
                  <NuxtLink to="/dashboard/companies">
                    My Company
                  </NuxtLink>
                </div>
              </div>
              <button
                class="flex items-center w-full text-sm text-left focus:outline-none flex-nowrap gap-x-2 disabled:cursor-not-allowed"
                :disabled="loggingOut"
                @click="logout"
              >
                <span class="text-slate-400">
                  <AnimatedCircleLoader v-if="loggingOut" />
                  <LogoutLarge
                    v-else
                    custom-style="size-5"
                  />
                </span> <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
        <!-- Current/Original Navbar -->
        <div
          v-else
          class="flex items-center gap-x-3"
        >
          <div class="flex items-center divide-x">
            <div
              v-if="user.is_zuno_engineer && userFiles.length > 0"
              class="relative navbar-dropdown inline-flex pr-3"
            >
              <button
                ref="downloadButtonRef"
                class="flex items-center justify-center w-8 h-8 transition duration-150 bg-gray-100 rounded-full hover:bg-gray-200"
                :class="{ 'text-gray-700 bg-gray-200': showItemNamesFilter }"
                @click="toggleDownloadsExpansion"
              >
                <span class="sr-only">Notifications</span>
                <span class="text-main-primary">
                  <CloudDownload />
                </span>
              </button>
              <div
                v-if="showItemNamesFilter"
                ref="downloadMenuRef"
                class="absolute w-[400px] right-0 top-full mt-1 dropdown-menu"
              >
                <div class="mt-8 bg-white border rounded-lg shadow-md border-slate-100">
                  <h4 class="py-4 mx-6 text-main-primary">
                    Downloads
                  </h4>
                  <hr class="h-0.5 bg-gray-100 border-0">
                  <div v-if="loading">
                    <LoaderComponent loader-text="Loading download files..." />
                  </div>
                  <div
                    v-else
                    class="relative max-h-[250px] pr-6 overflow-y-hidden hover:overflow-y-auto"
                  >
                    <div
                      v-for="(item, itemIndex) in listOfFiles"
                      :key="itemIndex"
                    >
                      <template v-if="item.status === 'pending' || item.status === 'completed'">
                        <div class="flex justify-between gap-3">
                          <div :class="`flex items-center gap-2  py-2 px-3 w-full text-primary`">
                            <div class="p-2 rounded-md bg-gray-50">
                              <ExcelIcon />
                            </div>
                            <div>
                              <p class="p-0 text-sm text-main-primary whitespace-nowrap">
                                {{ item.name }} <br>
                                <span class="p-0 text-xs">{{ $moment(item.created_on).format('DD/MM/YYYY HH:mm:ss')
                                }}</span>
                              </p>
                            </div>
                          </div>
                          <div
                            v-if="item.status === 'completed'"
                            class="flex items-center justify-between gap-x-3"
                          >
                            <button @click="downloadFile(item.file)">
                              <DownloadIcon custom-style="w-4 h-4 text-main-primary cursor-pointer" />
                            </button>
                            <button @click="deleteFile(item.id)">
                              <TrashIcon custom-style="w-4 h-4  text-red-700 cursor-pointer" />
                            </button>
                          </div>
                          <div
                            v-if="item.status === 'pending'"
                            class="flex items-center justify-between text-main-primary gap-x-3"
                          >
                            <SpinnerLarge />
                            <button @click="deleteFile(item.id)">
                              <TrashIcon custom-style="w-4 h-4  text-red-700 cursor-pointer" />
                            </button>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                  <hr class="h-0.5 my-4 bg-gray-100 border-0">
                  <div class="flex items-center justify-end w-full gap-2 px-4">
                    <button
                      class="px-4 py-2 mb-5 mr-3 text-sm text-white rounded-sm shadow-sm bg-main-primary"
                      @click="showItemNamesFilter = false"
                    >
                      Close
                    </button>
                    <!-- <PrimaryButton classes="px-16 py-1" @click="showItemNamesFilter = false;">Close</PrimaryButton> -->
                  </div>
                </div>
              </div>
            </div>
            <!-- Notifications button -->
            <div
              v-if="isDevelopment === 'development'"
              class="relative inline-flex px-3"
            >
              <button
                class="flex items-center justify-center w-8 h-8 transition duration-150 bg-gray-100 rounded-full hover:bg-gray-200"
              >
                <span class="sr-only">Notifications</span>
                <span class="text-gray-500">
                  <BellSmall />
                </span>
                <span class="absolute top-0 right-3 w-2.5 h-2.5 bg-red-500 border-2 border-white rounded-full" />
              </button>
            </div>
            <!-- Notifications button -->
            <div class="relative inline-flex pl-3 pr-3">
              <a
                href="https://zunocarbon.atlassian.net/servicedesk/customer/portals"
                target="_blank"
                title="Help Center"
                class="flex items-center justify-center w-8 h-8 transition duration-150 bg-gray-100 rounded-full hover:bg-gray-200"
              >
                <span class="sr-only">Help Center</span>
                <span class="text-gray-500"> ? </span>
              </a>
            </div>
            <!-- User -->
            <div class="relative hidden pl-3 md:block">
              <button
                class="inline-flex items-center justify-center Centergroup"
                aria-haspopup="true"
              >
                <span class="text-sm font-medium no-localize-translate group-hover:text-gray-800">{{ user.first_name }}
                  {{
                    user.last_name
                  }}</span>
              </button>
            </div>
          </div>

          <div class="relative navbar-dropdown">
            <button
              ref="dropdownButtonRef"
              class="flex items-center outline-none gap-x-1"
              @click="toggleDropdown"
            >
              <img
                v-if="subdomain === 'yokogawa'"
                src="https://res.cloudinary.com/dbacjggmt/image/upload/v1690978496/AHI_qrkfdd.jpg"
                alt="Veridis Logo"
                class="h-auto w-14"
              >
              <Avatar
                v-else
                class="w-10 h-10 rounded-full"
                variant="beam"
                :colors="colors"
                :name="user.first_name"
              />
              <svg
                class="flex-shrink-0 w-3 h-3 ml-1 text-gray-400 fill-current"
                viewBox="0 0 12 12"
              >
                <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
              </svg>
            </button>
            <div
              v-if="menuOpened"
              ref="dropdownMenuRef"
              class="absolute right-0 top-full min-w-[220px] mt-1 rounded-md p-4 bg-white border border-slate-300 flex flex-col gap-y-2 dropdown-menu"
            >
              <div class="flex flex-col pb-3 border-b gap-y-2">
                <span class="text-sm text-gray-300">Profile</span>
                <div class="flex flex-col text-sm gap-y-2">
                  <NuxtLink to="/dashboard/settings/general">
                    My Profile
                  </NuxtLink>
                  <NuxtLink to="/dashboard/companies">
                    My Company
                  </NuxtLink>
                </div>
              </div>
              <button
                class="flex items-center w-full text-sm text-left focus:outline-none flex-nowrap gap-x-2 disabled:cursor-not-allowed"
                :disabled="loggingOut"
                @click="logout"
              >
                <span class="text-slate-400">
                  <AnimatedCircleLoader v-if="loggingOut" />
                  <LogoutLarge
                    v-else
                    custom-style="size-5"
                  />
                </span> <span>Logout</span>
              </button>
            </div>
          </div>

          <!-- Language Picker -->
          <div
            class="relative inline-flex"
          >
            <LanguagePicker
              :user-current-language="currentLanguage"
              @language-changed="handleLanguageChange"
            />
          </div>
        </div>
      </div>
    </div>
    <TranslationWarningDialog
      @dismiss="handleWarningDismiss"
      @close="handleWarningClose"
    />
  </nav>
</template>

<script>
import Avatar from 'vue-boring-avatars'
import NotificationIcon from '@/components/NotificationIcon.vue'
import ChevronNav from '@/components/ChevronNav.vue'

const { setLanguage, currentLanguage } = useNuxtApp().$store.userSettings

export default {
  name: 'DashboardNavbarComponent',

  components: {
    Avatar,
    NotificationIcon,
    ChevronNav,
  },

  setup() {
    const {
      handleLanguageChangeWithLoading,
      dismissTranslationWarning,
      checkShouldShowWarning,
    } = useTranslationLoader()

    return {
      handleLanguageChangeWithLoading,
      dismissTranslationWarning,
      checkShouldShowWarning,
    }
  },

  data() {
    return {
      showItemNamesFilter: false,
      listOfFiles: [],
      userFiles: [],
      menuOpened: false,
      loading: false,
      colors: ['#FFAD08', '#EDD75A', '#73B06F', '#0C8F8F', '#405059'],
      subdomain: this.$store.companies.subdomain,
      currentLanguage: currentLanguage,
      showTranslationWidget: null,
      showRevampedNavbar: null,
      loggingOut: false,
      hasUnreadNotifications: null,
      dummyNotifications: [],
      dropdownMenuRef: null,
      dropdownButtonRef: null,
      downloadMenuRef: null,
      downloadButtonRef: null,
    }
  },

  computed: {
    user() {
      return this.$store.auth.authenticatedUser
    },
    isDevelopment() {
      return process.env.NODE_ENV === 'development'
    },
    zunoEngineerHasFiles() {
      return (
        this.$store.auth.authenticatedUser?.is_zuno_engineer
        && userFiles.length > 0
      )
    },
  },

  watch: {
    $route() {
      setTimeout(() => (this.opened = false), 500)
    },
  },

  mounted() {
    if (this.user.is_zuno_engineer) {
      this.fetchFiles()
    }
    this.showTranslationWidget = useFeatureFlag().isTranslationWidgetEnabled()
    this.showRevampedNavbar = useFeatureFlag().isRevampedNavbarEnabled()
    this.setupClickHandlers()
    this.currentLanguage = currentLanguage
    this.checkNotifications()
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick)
  },

  methods: {
    fetchFiles() {
      this.$apis.companies
        .listUserFiles(1)
        .then(({ data }) => (this.userFiles = data))
    },
    logout() {
      this.loggingOut = true
      setTimeout(() => this.$keycloak.logoutUser(), 500)
    },
    toggleDropdown() {
      // close any other open dropdown
      this.showItemNamesFilter = false
      // Then toggle this dropdown
      this.menuOpened = !this.menuOpened
    },
    downloadFile(link) {
      this.showItemNamesFilter = false
      window.open(link, '_blank')
    },
    deleteFile(id) {
      if (confirm('Do you really want to delete this file?')) {
        this.$apis.companies.deleteUserFile(id).then(() => {
          this.fetchFiles()
          this.$toast.success('File has been deleted', {
            timeout: 3000,
            position: 'top-right',
          })
        })
      }
    },
    toggleDownloadsExpansion() {
      this.menuOpened = false
      this.showItemNamesFilter = !this.showItemNamesFilter
      this.listOfFiles = []

      // Load files if needed
      if (this.showItemNamesFilter) {
        this.loading = true
        this.$apis.companies
          .listUserFiles(10)
          .then(({ data }) => (this.listOfFiles = data))
          .finally(() => (this.loading = false))
      }
    },
    async handleLanguageChange(languageCode) {
      // use the composable to handle translation loading
      await this.handleLanguageChangeWithLoading(languageCode, (code) => {
        setLanguage(code)
        this.currentLanguage = code
      })
    },
    handleWarningDismiss() {
      // user selected "Don't show again"
      this.dismissTranslationWarning()
    },
    handleWarningClose() {
      // user just closed the modal normally
      // warning can show again on next language change
    },
    checkNotifications() {
      // testing purposes - removed once sheet is finished
      // purely dummy - might change depend on backend data
      this.dummyNotifications = [
        {
          id: 1,
          read: true,
          status: 'downloaded',
        },
        {
          id: 2,
          read: false,
          status: 'ready',
        },
        {
          id: 3,
          read: true,
          status: 'failed',
        },
      ]

      // check for notifications/unread
      this.hasUnreadNotifications = this.dummyNotifications.some(
        notification => !notification.read,
      )
    },
    handleNotification() {
      // testing purposes - removed once sheet is finished
    },
    handleDocumentClick(event) {
      const profileButton = this.$refs.dropdownButtonRef
      const profileMenu = this.$refs.dropdownMenuRef
      const downloadButton = this.$refs.downloadButtonRef
      const downloadMenu = this.$refs.downloadMenuRef

      // simple check to see if the click is outside the dropdown
      if (this.menuOpened
        && profileButton
        && profileMenu
        && !profileButton.contains(event.target)
        && !profileMenu.contains(event.target)) {
        this.menuOpened = false
      }

      // simple check to see if the click is outside both button and menu
      if (this.showItemNamesFilter
        && downloadButton
        && downloadMenu
        && !downloadButton.contains(event.target)
        && !downloadMenu.contains(event.target)) {
        this.showItemNamesFilter = false
      }
    },
    setupClickHandlers() {
      document.addEventListener('click', this.handleDocumentClick)
    },
  },
}
</script>

<style scoped>
.navbar-dropdown {
  z-index: 10;
  position: relative;
}

.navbar-content {
  z-index: 10;
}

.dropdown-menu {
  z-index: 30;
  position: absolute;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}
</style>
